import { cloneDeep, isEqual } from "lodash-es"
import { reaction, toJS } from "mobx"
import { observer } from "mobx-react"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { APICode } from "@/api/APICode"
import TPAPI from "@/api/tracking-page"
import SaveModule from "@/component/SaveModule"
import { useEditorShortcuts } from "@/hooks/useEditorShortcuts"
import type {
  ThemeBottomSectionImageItem,
  ThemeBottomSectionState,
  ThemeBottomSectionVideo,
  ThemeImageBannerRef,
} from "@/models/ITrackingPageOptions"
import { ThemeLayoutEnum } from "@/models/ITrackingPageOptions"
import type {
  TrackingPageThemeBottomSectionContent,
  TrackingPageThemeBottomSectionVideoItem,
  TrackingPageThemeImageApiValue,
  TrackingPageThemeLayoutInlinePayload,
  TrackingPageThemeMediaImage,
  TrackingPageThemeShowcaseColumn,
  TrackingPageThemeShowcaseColumnImage,
} from "@/models/ITrackingPageThemeLayoutConfig"
import { emptyTrackingPageThemeMediaImage } from "@/models/ITrackingPageThemeLayoutConfig"
import { TrackingPageThemeContentColumnType } from "@/models/ITrackingPageThemeLayoutConfig"
import type { ThemeLayoutBackdropEditorDraft } from "@/pages/TrackingPage/ThemeLayoutCustomize/components/Layout/ThemeLayoutLeftTabPanels/themeLayoutBackdropEditor"
import {
  createInitialBackdropEditorDraft,
} from "@/pages/TrackingPage/ThemeLayoutCustomize/components/Layout/ThemeLayoutLeftTabPanels/themeLayoutBackdropEditor"
import type { ThemeLayoutShowcaseContentDraft } from "@/pages/TrackingPage/ThemeLayoutCustomize/components/Layout/ThemeLayoutLeftTabPanels/themeLayoutShowcaseDraft"
import {
  createInitialShowcaseDraft,
  reconcileTopColumnOrder,
  SHOWCASE_TOP_ORDER_LOOKUP_ROW_ID,
} from "@/pages/TrackingPage/ThemeLayoutCustomize/components/Layout/ThemeLayoutLeftTabPanels/themeLayoutShowcaseDraft"
import { ThemeLayoutSubmoduleDirtyProvider } from "@/pages/TrackingPage/ThemeLayoutCustomize/components/Layout/ThemeLayoutLeftTabPanels/ThemeLayoutSubmoduleDirtyContext"
import ThemeLayoutCustomizeAppWindowShell from "@/pages/TrackingPage/ThemeLayoutCustomize/ThemeLayoutCustomizeAppWindowShell"
import ThemeLayoutCustomizeEditor from "@/pages/TrackingPage/ThemeLayoutCustomize/ThemeLayoutCustomizeEditor"
import {
  fetchTrackingPageBaseData,
  fetchTrackingPageProductCollects,
} from "@/pages/TrackingPage/trackingPageDataFetch"
import stores from "@/stores"
import {
  normalizeSavedThemeConfigPayload,
  normalizeTrackingPageThemeConfigGetData,
} from "@/stores/tracking_page/ThemeLayoutConfigStore"
import { createUndoHistory } from "@/utils/createUndoHistory"
import { removeShopifyMediaImageGidsFromPayload } from "@/utils/pruneThemeLayoutMissingShopifyMediaImages"
import { prepareThemeLayoutPayloadForSave } from "@/utils/themeLayoutThemeImageField"
import toast from "@/utils/toast"

export interface ThemeLayoutCustomizePageContentProps {
  variant: ThemeLayoutEnum
}

const mapThemeImageToDraft = (
  image: TrackingPageThemeImageApiValue,
): ThemeImageBannerRef | undefined => {
  const row = Array.isArray(image) ? image[0] : image

  if (!row?.gid?.trim() && !row?.url?.trim()) {
    // 无真实图片但有 alt 文本时，保留 stub，使 alt 可被正常读取与编辑
    return row?.alt?.trim() ? { shopifyFileId: "", url: "", alt: row.alt } : undefined
  }

  return {
    shopifyFileId: row.gid,
    url: row.url,
    alt: row.alt,
  }
}

const mapThemeImageToConfig = (image: ThemeImageBannerRef | undefined): TrackingPageThemeMediaImage => {
  if (!image?.shopifyFileId?.trim() && !image?.url?.trim()) {
    // 无真实图片时仍保留 alt 字段，确保 alt 能完整往返于 config ↔ draft
    return { ...emptyTrackingPageThemeMediaImage(), alt: image?.alt || "" }
  }

  return {
    gid: image.shopifyFileId,
    url: image.url,
    alt: image.alt || "",
  }
}

const mapBottomSectionToDraft = (section: TrackingPageThemeBottomSectionContent): ThemeBottomSectionState => {
  // id 必须跟随元素本身、而非数组下标：showcaseDraft 每次变更都会从 store 重新
  // 派生，若用 `img-${index}` 则 id 恒定绑在位置上，拖拽后 React key 顺序不变、
  // DOM 不重排，拖动元素会视觉弹回原位（顺序看似未变）。
  // 优先用持久化到 config 的 id（添加时生成的 UUID，空行也有），其次 gid，最后回退下标。
  const images = section.images.map((item, index) => ({
    id: item.id?.trim() || item.image?.gid?.trim() || `img-${index}`,
    image: mapThemeImageToDraft(item.image),
    destinationUrl: item.destination_url,
    openInNewWindow: item.open_new_window,
    enabled: item.enabled,
  }))
  const firstVideo = section.videos[0]
  const video: ThemeBottomSectionVideo | undefined = firstVideo
    ? {
      id: "video-0",
      youtubeUrl: firstVideo.url,
      alt: firstVideo.alt,
      autoplay: firstVideo.auto_play,
      loop: firstVideo.loop_play,
      enabled: firstVideo.enabled,
    }
    : undefined

  return {
    images,
    video,
    imageRatio: section.image_ratio,
  }
}

const mapBottomSectionToConfig = (
  section: ThemeBottomSectionState,
  previousSection?: TrackingPageThemeBottomSectionContent,
): TrackingPageThemeBottomSectionContent => {
  const preservedVideos: TrackingPageThemeBottomSectionVideoItem[] = previousSection?.videos.slice(1) || []
  const primaryVideo: TrackingPageThemeBottomSectionVideoItem[] = section.video ? [{
    type: "video",
    enabled: section.video.enabled !== false,
    url: section.video.youtubeUrl,
    alt: section.video.alt || "",
    auto_play: section.video.autoplay,
    loop_play: section.video.loop,
  }] : []

  return {
    image_ratio: section.imageRatio,
    images: section.images.map((item) => ({
      type: "image",
      // 把 draft 的稳定 id 持久化进 config，使其穿过 draft→config→store→draft 往返，
      // 从而让拖拽排序（含无图空行）的顺序得以保留。
      id: item.id,
      enabled: item.enabled !== false,
      image: mapThemeImageToConfig(item.image),
      destination_url: item.destinationUrl,
      open_new_window: item.openInNewWindow,
    })),
    videos: [...primaryVideo, ...preservedVideos],
  }
}

const normalizeWidth = (width: string): "33" | "50" | "67" | "100" => {
  const widthPercent = width.replace("%", "")

  if (widthPercent === "33" || widthPercent === "50" || widthPercent === "67") {
    return widthPercent
  }

  return "100"
}

const mapBackdropDraftFromConfig = (
  config: TrackingPageThemeLayoutInlinePayload | undefined,
): ThemeLayoutBackdropEditorDraft => {
  if (!config || "columns" in config.content || !("layout" in config.style.order_lookup_widget)) {
    return createInitialBackdropEditorDraft()
  }

  return {
    content: {
      mobile_layout: config.content.top_section.image_banner.mobile_layout,
      bottom_section: mapBottomSectionToDraft(config.content.bottom_section),
      image_banner: mapThemeImageToDraft(config.content.top_section.image_banner.image),
    },
    style: {
      corner_radius: config.style.order_lookup_widget.display.corner_radius,
      desktop_bg_color: config.style.order_lookup_widget.layout.desktop_bg_color.replace("#", ""),
      position: config.style.order_lookup_widget.layout.position,
      size: config.style.order_lookup_widget.layout.size,
    },
  }
}

const mapShowcaseDraftFromConfig = (
  config: TrackingPageThemeLayoutInlinePayload | undefined,
): ThemeLayoutShowcaseContentDraft => {
  if (!config || !("columns" in config.content) || !("width" in config.style.order_lookup_widget)) {
    return createInitialShowcaseDraft()
  }

  const topSectionColumnOrder: string[] = []
  const topSectionImages: ThemeBottomSectionImageItem[] = []

  config.content.columns.forEach((column, index) => {
    if (column.type === TrackingPageThemeContentColumnType.OrderLookupWidget) {
      topSectionColumnOrder.push(SHOWCASE_TOP_ORDER_LOOKUP_ROW_ID)
      return
    }
    if (column.type === TrackingPageThemeContentColumnType.Image) {
      // 不能用 gid 当 id：多列共用同一张图片时 gid 相同会导致 id 冲突，
      // 出现「勾一个全勾、改一个全改」。优先用持久化的稳定 id，回退到唯一下标。
      const id = column.id?.trim() || `top-img-${index}`

      topSectionColumnOrder.push(id)
      topSectionImages.push({
        id,
        image: mapThemeImageToDraft(column.image),
        destinationUrl: column.destination_url,
        openInNewWindow: column.open_new_window,
        enabled: column.enabled,
      })
    }
  })

  return {
    top_section_images: topSectionImages,
    top_section_column_order: reconcileTopColumnOrder(topSectionColumnOrder, topSectionImages),
    bottom_section: mapBottomSectionToDraft(config.content.bottom_section),
    width: normalizeWidth(config.style.order_lookup_widget.width),
    corner_radius: config.style.order_lookup_widget.display.corner_radius,
  }
}

const createBackdropConfigFromDraft = (
  draft: ThemeLayoutBackdropEditorDraft,
  previousConfig?: TrackingPageThemeLayoutInlinePayload,
): TrackingPageThemeLayoutInlinePayload => ({
  theme: 2,
  content: {
    top_section: {
      image_banner: {
        image: mapThemeImageToConfig(draft.content.image_banner),
        mobile_layout: draft.content.mobile_layout,
      },
    },
    bottom_section: mapBottomSectionToConfig(
      draft.content.bottom_section,
      previousConfig && !("columns" in previousConfig.content) ? previousConfig.content.bottom_section : undefined,
    ),
  },
  style: {
    order_lookup_widget: {
      display: {
        corner_radius: draft.style.corner_radius,
      },
      layout: {
        size: draft.style.size,
        position: draft.style.position,
        desktop_bg_color: `#${draft.style.desktop_bg_color.replace("#", "").toUpperCase()}`,
      },
    },
  },
})

const mapShowcaseTopImageToColumn = (
  item: ThemeBottomSectionImageItem,
): TrackingPageThemeShowcaseColumnImage => ({
  type: TrackingPageThemeContentColumnType.Image,
  // 把 draft 的稳定 id 持久化进 config，使其穿过 draft→config→store→draft 往返，
  // 让拖拽排序与逐列编辑在多列同图时仍保持唯一身份（发送给后端前会被剥离）。
  id: item.id,
  enabled: item.enabled !== false,
  image: mapThemeImageToConfig(item.image),
  destination_url: item.destinationUrl,
  open_new_window: item.openInNewWindow,
})

const createShowcaseConfigFromDraft = (
  draft: ThemeLayoutShowcaseContentDraft,
  previousConfig?: TrackingPageThemeLayoutInlinePayload,
): TrackingPageThemeLayoutInlinePayload => {
  const imageById = new Map(draft.top_section_images.map((item) => [item.id, item]))
  const columnOrder = reconcileTopColumnOrder(draft.top_section_column_order, draft.top_section_images)
  const columns: TrackingPageThemeShowcaseColumn[] = []

  for (const id of columnOrder) {
    if (id === SHOWCASE_TOP_ORDER_LOOKUP_ROW_ID) {
      columns.push({
        type: TrackingPageThemeContentColumnType.OrderLookupWidget,
        enabled: true,
      })
      continue
    }
    const item = imageById.get(id)

    if (item) {
      columns.push(mapShowcaseTopImageToColumn(item))
    }
  }

  return {
    theme: 3,
    content: {
      columns,
      bottom_section: mapBottomSectionToConfig(
        draft.bottom_section,
        previousConfig && "columns" in previousConfig.content ? previousConfig.content.bottom_section : undefined,
      ),
    },
    style: {
      order_lookup_widget: {
        display: {
          corner_radius: draft.corner_radius,
        },
        width: `${draft.width}%`,
      },
    },
  }
}

/**
 * 主题定制实际 UI（`s-page` 顶栏插槽 + Polaris 左右栏），在 App Window 的 iframe 内或非嵌入环境全屏展示。
 */
const ThemeLayoutCustomizePageContent = observer(({ variant }: ThemeLayoutCustomizePageContentProps) => {
  const { t } = useTranslation(["tracking_page", "common"])
  const { themeLayoutConfigStore } = stores
  const [saveLoading, setSaveLoading] = useState(false)
  const [submoduleDirtyMap, setSubmoduleDirtyMap] = useState<Record<string, boolean>>({})
  const [previewUrl, setPreviewUrl] = useState<string>("")
  const showcaseDraft = useMemo(
    () => mapShowcaseDraftFromConfig(themeLayoutConfigStore.showcaseConfig),
    [themeLayoutConfigStore.showcaseConfig],
  )
  const backdropDraft = useMemo(
    () => mapBackdropDraftFromConfig(themeLayoutConfigStore.backdropConfig),
    [themeLayoutConfigStore.backdropConfig],
  )
  const initialShowcaseDraftRef = useRef<ThemeLayoutShowcaseContentDraft>(cloneDeep(showcaseDraft))
  const initialBackdropDraftRef = useRef<ThemeLayoutBackdropEditorDraft>(cloneDeep(backdropDraft))
  const isShowcaseVariant = variant === ThemeLayoutEnum.SHOWCASE
  // 等首次接口请求完毕、initialDraft baseline 写好之后，才开放 dirty 监听；
  // 否则 fetch 回来的 store 更新会让 draft useMemo 先于 ref 更新，hasUnsavedChanges 误报 true。
  const [isInitialized, setIsInitialized] = useState(false)

  const hasUnsavedChanges = useMemo(() => {
    if (!isInitialized) {
      return false
    }
    if (isShowcaseVariant) {
      return !isEqual(showcaseDraft, initialShowcaseDraftRef.current)
    }

    return !isEqual(backdropDraft, initialBackdropDraftRef.current)
  }, [backdropDraft, isInitialized, isShowcaseVariant, showcaseDraft])
  const isSubmoduleDirty = useMemo(
    () => Object.values(submoduleDirtyMap).some(Boolean),
    [submoduleDirtyMap],
  )
  // 撤销 / 重做历史：store 中当前变体的 config 每次编辑都会整体替换，按引用变化记录
  const history = useMemo(() => createUndoHistory<TrackingPageThemeLayoutInlinePayload | undefined>({
    getSnapshot: () => toJS(isShowcaseVariant ? themeLayoutConfigStore.showcaseConfig : themeLayoutConfigStore.backdropConfig),
    applySnapshot: (snapshot) => {
      if (!snapshot) {
        return
      }
      if (isShowcaseVariant) {
        themeLayoutConfigStore.setShowcaseConfig(snapshot)
      } else {
        themeLayoutConfigStore.setBackdropConfig(snapshot)
      }
    },
  }), [isShowcaseVariant, themeLayoutConfigStore])

  useEffect(() => reaction(
    () => (isShowcaseVariant ? themeLayoutConfigStore.showcaseConfig : themeLayoutConfigStore.backdropConfig),
    () => history.record(),
  ), [history, isShowcaseVariant, themeLayoutConfigStore])

  // 首次数据就绪后以其为基线
  useEffect(() => {
    if (isInitialized) {
      history.reset()
    }
  }, [history, isInitialized])

  const handleSubmoduleDirtyChange = useCallback((key: string, dirty: boolean) => {
    setSubmoduleDirtyMap((prev) => {
      if ((prev[key] ?? false) === dirty) {
        return prev
      }

      return { ...prev, [key]: dirty }
    })
  }, [])

  useEffect(() => {
    let isUnmounted = false

    setIsInitialized(false)
    const loadConfig = async () => {
      try {
        await themeLayoutConfigStore.fetchThemeConfigByVariant(variant)
      } finally {
        if (isUnmounted) {
          return
        }
        if (isShowcaseVariant) {
          initialShowcaseDraftRef.current = cloneDeep(mapShowcaseDraftFromConfig(themeLayoutConfigStore.showcaseConfig))
        } else {
          initialBackdropDraftRef.current = cloneDeep(mapBackdropDraftFromConfig(themeLayoutConfigStore.backdropConfig))
        }
        setIsInitialized(true)
      }
    }

    void loadConfig()

    return () => {
      isUnmounted = true
    }
  }, [isShowcaseVariant, themeLayoutConfigStore, variant])

  // 从其他窗口/标签页切回时，重新同步图片引用：别处删除的图片会被就地清除。
  useEffect(() => {
    if (!isInitialized) {
      return
    }

    const handleRefocus = () => {
      if (document.visibilityState !== "visible") {
        return
      }
      void (async () => {
        const pruned = await themeLayoutConfigStore.syncCurrentThemeImages(variant)

        // 剪枝后把基线（原配置）同步为当前配置，使删除图片的改动不计入未保存差异、不触发全局保存
        if (pruned) {
          if (isShowcaseVariant) {
            initialShowcaseDraftRef.current = cloneDeep(
              mapShowcaseDraftFromConfig(themeLayoutConfigStore.showcaseConfig),
            )
          } else {
            initialBackdropDraftRef.current = cloneDeep(
              mapBackdropDraftFromConfig(themeLayoutConfigStore.backdropConfig),
            )
          }
          // 已删除的图片不允许通过撤销恢复
          history.reset()
        }
      })()
    }

    window.addEventListener("focus", handleRefocus)
    document.addEventListener("visibilitychange", handleRefocus)

    return () => {
      window.removeEventListener("focus", handleRefocus)
      document.removeEventListener("visibilitychange", handleRefocus)
    }
  }, [history, isInitialized, isShowcaseVariant, themeLayoutConfigStore, variant])

  // 在应用内（图库弹窗）删除某张图片后，遍历当前页面配置，移除其它仍引用该 gid 的地方：
  // - active 数据：就地置空被删图片引用（编辑器/预览随 store 更新自动刷新）；
  // - 元数据（基线）：仅就地移除该 gid 对应的引用，使「这次删除」不计入未保存差异、不触发全局保存。
  //   若页面本就处于修改态（存在其它未保存改动），其差异因只动了被删 gid 而原样保留，仍为修改态。
  const handleCommonImageDeleted = useCallback(
    (gid: string) => {
      if (!gid) {
        return
      }

      // 1) active 数据
      themeLayoutConfigStore.removeShopifyMediaImageRefs(variant, [gid])

      // 2) 元数据（基线）：把基线 draft 还原为 config，仅移除被删 gid 后再映射回 draft 写回基线
      if (isShowcaseVariant) {
        const baseConfig = createShowcaseConfigFromDraft(
          cloneDeep(initialShowcaseDraftRef.current),
          themeLayoutConfigStore.showcaseConfig,
        )
        const { payload, modified } = removeShopifyMediaImageGidsFromPayload(baseConfig, [gid])

        if (modified) {
          initialShowcaseDraftRef.current = cloneDeep(mapShowcaseDraftFromConfig(payload))
        }
      } else {
        const baseConfig = createBackdropConfigFromDraft(
          cloneDeep(initialBackdropDraftRef.current),
          themeLayoutConfigStore.backdropConfig,
        )
        const { payload, modified } = removeShopifyMediaImageGidsFromPayload(baseConfig, [gid])

        if (modified) {
          initialBackdropDraftRef.current = cloneDeep(mapBackdropDraftFromConfig(payload))
        }
      }

      // 已删除的图片不允许通过撤销恢复
      history.reset()
    },
    [history, isShowcaseVariant, themeLayoutConfigStore, variant],
  )

  useEffect(() => {
    if (!isInitialized) {
      return
    }

    return themeLayoutConfigStore.onCommonImageDeleted(handleCommonImageDeleted)
  }, [handleCommonImageDeleted, isInitialized, themeLayoutConfigStore])

  useEffect(() => {
    let isUnmounted = false

    const fetchThemePreview = async () => {
      try {
        const { data } = await TPAPI.getThemePreview(variant)

        if (isUnmounted) {
          return
        }
        setPreviewUrl(data.tracking_page_theme_preview.url)
      } catch (error) {
        if (isUnmounted) {
          return
        }
      }
    }

    void fetchThemePreview()

    return () => {
      isUnmounted = true
    }
  }, [variant])

  useEffect(() => {
    document.body.classList.add("pp-theme-layout-customize")

    return () => {
      document.body.classList.remove("pp-theme-layout-customize")
    }
  }, [])

  const handleSaveAction = useCallback(async () => {
    setSaveLoading(true)
    try {
      const config = isShowcaseVariant
        ? createShowcaseConfigFromDraft(showcaseDraft, themeLayoutConfigStore.showcaseConfig)
        : createBackdropConfigFromDraft(backdropDraft, themeLayoutConfigStore.backdropConfig)
      const { data: respBody, status } = await TPAPI.saveThemeConfig(
        variant,
        prepareThemeLayoutPayloadForSave(config),
      )

      if (status !== APICode.OK) {
        return
      }

      if (respBody.code && respBody.code !== APICode.OK){
        respBody.msg && toast(respBody.msg, 2000, true)
        return
      }

      const envelope = normalizeSavedThemeConfigPayload(respBody)
      const normalized = envelope ? normalizeTrackingPageThemeConfigGetData(envelope) : undefined

      if (normalized) {
        // 后端不持久化前端排序用的 id，但保存保持了图片数组顺序。按下标把发送前的
        // 稳定 id 回贴到 normalized，使保存后同一会话内继续拖拽（含无图空行）仍保持
        // 身份、不弹回。同时让 store 与 initialRef 基线 id 一致，避免脏检查误报。
        const sentBottomImages = config.content.bottom_section.images

        normalized.content.bottom_section.images.forEach((img, i) => {
          img.id = sentBottomImages[i]?.id
        })

        // Showcase 顶部列同理：后端不持久化前端 id，但保持了列顺序，按下标把发送前的
        // 稳定 id 回贴，使保存后同一会话内继续拖拽/逐列编辑（含多列同图）仍保持唯一身份。
        if ("columns" in normalized.content && "columns" in config.content) {
          const sentColumns = config.content.columns

          normalized.content.columns.forEach((column, i) => {
            const sent = sentColumns[i]

            if (
              column.type === TrackingPageThemeContentColumnType.Image
              && sent?.type === TrackingPageThemeContentColumnType.Image
            ) {
              column.id = sent.id
            }
          })
        }

        if (isShowcaseVariant) {
          themeLayoutConfigStore.setShowcaseConfig(normalized)
          initialShowcaseDraftRef.current = cloneDeep(mapShowcaseDraftFromConfig(normalized))
        } else {
          themeLayoutConfigStore.setBackdropConfig(normalized)
          initialBackdropDraftRef.current = cloneDeep(mapBackdropDraftFromConfig(normalized))
        }
      } else {
        if (isShowcaseVariant) {
          initialShowcaseDraftRef.current = cloneDeep(showcaseDraft)
        } else {
          initialBackdropDraftRef.current = cloneDeep(backdropDraft)
        }
      }

      stores.commonStore.setContextualSaveBar("loading", false)
      stores.commonStore.setContextualSaveBar("show", false)
      history.reset()
      toast(String(t("common:SavedSuccess")), 2e3)
    } finally {
      setSaveLoading(false)
    }
  }, [
    backdropDraft,
    history,
    isShowcaseVariant,
    showcaseDraft,
    t,
    themeLayoutConfigStore,
    variant,
  ])

  const handleDiscardAction = useCallback(() => {
    if (isShowcaseVariant) {
      const newConfig = createShowcaseConfigFromDraft(
        cloneDeep(initialShowcaseDraftRef.current),
        themeLayoutConfigStore.showcaseConfig,
      )

      themeLayoutConfigStore.setShowcaseConfig(newConfig)
      // 将 initialDraft 对齐到经过同一 map 管道后实际得到的值，
      // 避免 draft→config→draft 的往返变换（id 规则、alt||"" 等）产生微小差异，
      // 导致 isEqual 仍为 false、hasUnsavedChanges 无法回到 false。
      initialShowcaseDraftRef.current = cloneDeep(mapShowcaseDraftFromConfig(newConfig))
    } else {
      const newConfig = createBackdropConfigFromDraft(
        cloneDeep(initialBackdropDraftRef.current),
        themeLayoutConfigStore.backdropConfig,
      )

      themeLayoutConfigStore.setBackdropConfig(newConfig)
      initialBackdropDraftRef.current = cloneDeep(mapBackdropDraftFromConfig(newConfig))
    }
    history.reset()
  }, [history, isShowcaseVariant, themeLayoutConfigStore])

  const setShowcaseDraft: React.Dispatch<React.SetStateAction<ThemeLayoutShowcaseContentDraft>> = useCallback(
    (value) => {
      const nextDraft = typeof value === "function" ? value(showcaseDraft) : value

      themeLayoutConfigStore.setShowcaseConfig(
        createShowcaseConfigFromDraft(nextDraft, themeLayoutConfigStore.showcaseConfig),
      )
    },
    [showcaseDraft, themeLayoutConfigStore, themeLayoutConfigStore.showcaseConfig],
  )

  const setBackdropDraft: React.Dispatch<React.SetStateAction<ThemeLayoutBackdropEditorDraft>> = useCallback(
    (value) => {
      const nextDraft = typeof value === "function" ? value(backdropDraft) : value

      themeLayoutConfigStore.setBackdropConfig(
        createBackdropConfigFromDraft(nextDraft, themeLayoutConfigStore.backdropConfig),
      )
    },
    [backdropDraft, themeLayoutConfigStore, themeLayoutConfigStore.backdropConfig],
  )

  const heading =
    variant === ThemeLayoutEnum.BACKDROP
      ? t("tracking_page:customizeBackdrop")
      : t("tracking_page:customizeShowcase")

  const showSaveBar = hasUnsavedChanges && !isSubmoduleDirty

  // 撤销 / 重做（保存快捷键由 SaveModule 统一处理）；子模块编辑中时不响应，避免与其未提交的修改交错
  useEditorShortcuts({
    enabled: isInitialized && !isSubmoduleDirty && !saveLoading,
    onUndo: () => history.undo(),
    onRedo: () => history.redo(),
  })

  return (
    <ThemeLayoutSubmoduleDirtyProvider value={{ setSubmoduleDirty: handleSubmoduleDirtyChange }}>
      <ThemeLayoutCustomizeAppWindowShell
        heading={String(heading)}
        previewUrl={previewUrl}
        showSaveBar={showSaveBar}
        onSave={handleSaveAction}
        onDiscard={handleDiscardAction}
        saveLoading={saveLoading}
        saveDisabled={false}
      >
        <SaveModule
          subRouteBlock
          onSave={handleSaveAction}
          onDiscard={handleDiscardAction}
          show={showSaveBar}
          loading={saveLoading}
          disabled={false}
        />
        <ThemeLayoutCustomizeEditor
          variant={variant}
          backdropDraft={backdropDraft}
          setBackdropDraft={setBackdropDraft}
          showcaseDraft={showcaseDraft}
          setShowcaseDraft={setShowcaseDraft}
        />
      </ThemeLayoutCustomizeAppWindowShell>
    </ThemeLayoutSubmoduleDirtyProvider>
  )
})

export default ThemeLayoutCustomizePageContent
